/**
 * `/organizations/{id}/admin` — screen 22.
 *
 * ## The sentence this screen exists to keep true
 *
 * > Organization Administration governs the **Organization**. It governs nobody's celebrations.
 *
 * A diocesan administrator may correct the diocese's profile, set its customary defaults, and say
 * who else administers it. They cannot see a parish's funeral, cannot approve a selection, and
 * cannot grant themselves access to either. There is no route behind this screen that reads private
 * planning content — the same shape as Site Administration, one level down.
 *
 * ## Defaults are not constraints
 *
 * The distinction people most often get wrong, and the reason the Defaults pane says so twice: a
 * Default is a **starting point somebody may change**. Only a Template Constraint blocks a
 * Finalization. An administrator who believes otherwise will try to enforce parish practice here and
 * quietly fail.
 *
 * A **403 rather than 404** for somebody without authority, unlike almost everywhere else: the
 * Organization is in the public registry, so concealing it would be incoherent. Existence is not
 * secret here.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { useAuth } from '../auth/useAuth';
import {
  assignAdministrator,
  fetchOrganization,
  fetchSiteAccounts,
  ORGANIZATION_KIND_LABELS,
  revokeAdministrator,
  saveDefaults,
  saveProfile,
  type OrganizationDetail,
} from './api';
import './organizations.css';

type Pane = 'profile' | 'defaults' | 'administrators';

export function OrganizationAdminScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/organizations/$id/admin' });
  const { t } = useTranslation('common');
  const [pane, setPane] = useState<Pane>('profile');

  const panes: { value: Pane; label: string }[] = [
    { value: 'profile', label: t('organizations.admin.tab_profile') },
    { value: 'defaults', label: t('organizations.admin.tab_defaults') },
    { value: 'administrators', label: t('organizations.admin.tab_admins') },
  ];

  const organization = useQuery({
    queryKey: ['organization', id],
    queryFn: () => fetchOrganization(id),
  });

  const error = organization.error instanceof Error ? (organization.error as ApiError) : undefined;

  if (organization.isPending) {
    return (
      <div className="org">
        <p className="org__fine">{t('organizations.loading')}</p>
      </div>
    );
  }

  const detail = organization.data?.data;

  if (!detail) {
    return (
      <div className="org">
        {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
        <p className="org__fine">
          <Link to="/organizations">{t('organizations.admin.back_registry')}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="org">
      <header className="org__head">
        <div>
          <p className="org__eyebrow">
            {ORGANIZATION_KIND_LABELS[detail.kind]}
            {detail.parent_name ? ` · ${detail.parent_name}` : ''}
          </p>
          <h1 className="org__title">{detail.name}</h1>
          <p className="org__lede">{t('organizations.admin.governance_lede')}</p>
        </div>
        <Link className="org__link" to="/organizations">
          {t('organizations.admin.registry_link')}
        </Link>
      </header>

      {/* Read-only rather than refused. Somebody who can see the registry entry can read its
          profile; only writing is restricted, and every write route enforces that server-side. */}
      {!detail.may_administer ? (
        <p className="org__found">{t('organizations.admin.not_admin', { name: detail.name })}</p>
      ) : null}

      <nav className="org__tabs">
        {panes.map((entry) => (
          <button
            key={entry.value}
            type="button"
            className={`org__tab${pane === entry.value ? ' org__tab--on' : ''}`}
            onClick={() => setPane(entry.value)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <div className="org__grid">
        <div className="org__main">
          {pane === 'profile' ? (
            <ProfilePane detail={detail} />
          ) : pane === 'defaults' ? (
            <DefaultsPane detail={detail} />
          ) : (
            <AdministratorsPane detail={detail} />
          )}
        </div>

        <aside className="org__side">
          <section className="org__card org__card--warm">
            <p className="org__eyebrow">{t('organizations.admin.authority_not_title')}</p>
            <p className="org__body">{t('organizations.admin.authority_not_desc')}</p>
            <p className="org__body org__body--strong">
              {t('organizations.admin.authority_not_strong')}
            </p>
            <p className="org__fine">{t('organizations.admin.authority_not_fine')}</p>
          </section>

          <section className="org__card">
            <p className="org__eyebrow">{t('organizations.admin.recorded_title')}</p>
            <p className="org__body">{t('organizations.admin.recorded_desc')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

// ── profile ─────────────────────────────────────────────────────────────────

const PROFILE_FIELD_CONFIG: { key: string; labelKey: string; hintKey?: string }[] = [
  { key: 'locality', labelKey: 'organizations.admin.city_town' },
  { key: 'region', labelKey: 'organizations.admin.state_region' },
  { key: 'country', labelKey: 'organizations.admin.country' },
  { key: 'website', labelKey: 'organizations.admin.website' },
  {
    key: 'contact_email',
    labelKey: 'organizations.admin.contact_address',
    hintKey: 'organizations.admin.contact_hint',
  },
];

function ProfilePane({ detail }: { detail: OrganizationDetail }): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<Record<string, unknown>>(detail.profile ?? {});

  useEffect(() => setProfile(detail.profile ?? {}), [detail.profile]);

  const save = useMutation({
    mutationFn: () => saveProfile(detail.id, profile),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization', detail.id] });
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const error = save.error instanceof Error ? (save.error as ApiError) : undefined;

  return (
    <section className="org__card">
      <p className="org__eyebrow">{t('organizations.admin.tab_profile')}</p>
      <p className="org__fine">{t('organizations.admin.profile_desc')}</p>

      {PROFILE_FIELD_CONFIG.map((field) => (
        <label key={field.key} className="org__field">
          <span className="org__field-label">{t(field.labelKey)}</span>
          <input
            value={typeof profile[field.key] === 'string' ? (profile[field.key] as string) : ''}
            disabled={!detail.may_administer}
            onChange={(e) => setProfile({ ...profile, [field.key]: e.target.value })}
          />
          {field.hintKey ? <span className="org__fine">{t(field.hintKey)}</span> : null}
        </label>
      ))}

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      {detail.may_administer ? (
        <div className="org__actions">
          <button
            type="button"
            className="org__btn"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? t('organizations.admin.saving')
              : t('organizations.admin.save_profile')}
          </button>
          {save.isSuccess ? (
            <span className="org__fine">{t('organizations.admin.saved')}</span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ── defaults ────────────────────────────────────────────────────────────────

const DEFAULT_FIELD_CONFIG: { key: string; labelKey: string; hintKey: string }[] = [
  {
    key: 'liturgical_language',
    labelKey: 'organizations.admin.customary_lang',
    hintKey: 'organizations.admin.customary_lang_hint',
  },
  {
    key: 'export_profile',
    labelKey: 'organizations.admin.usual_export_profile',
    hintKey: 'organizations.admin.usual_export_profile_hint',
  },
  {
    key: 'note',
    labelKey: 'organizations.admin.note_planners',
    hintKey: 'organizations.admin.note_planners_hint',
  },
];

function DefaultsPane({ detail }: { detail: OrganizationDetail }): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [defaults, setDefaults] = useState<Record<string, unknown>>(detail.defaults ?? {});

  useEffect(() => setDefaults(detail.defaults ?? {}), [detail.defaults]);

  const save = useMutation({
    mutationFn: () => saveDefaults(detail.id, defaults),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization', detail.id] });
    },
  });

  const error = save.error instanceof Error ? (save.error as ApiError) : undefined;

  return (
    <section className="org__card">
      <p className="org__eyebrow">{t('organizations.admin.tab_defaults')}</p>

      <p className="org__body org__body--strong">{t('organizations.admin.defaults_heading')}</p>
      <p className="org__fine">{t('organizations.admin.defaults_fine')}</p>

      {DEFAULT_FIELD_CONFIG.map((field) => (
        <label key={field.key} className="org__field">
          <span className="org__field-label">{t(field.labelKey)}</span>
          <input
            value={typeof defaults[field.key] === 'string' ? (defaults[field.key] as string) : ''}
            disabled={!detail.may_administer}
            onChange={(e) => setDefaults({ ...defaults, [field.key]: e.target.value })}
          />
          <span className="org__fine">{t(field.hintKey)}</span>
        </label>
      ))}

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      {detail.may_administer ? (
        <div className="org__actions">
          <button
            type="button"
            className="org__btn"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? t('organizations.admin.saving')
              : t('organizations.admin.save_defaults')}
          </button>
          {save.data ? <span className="org__fine">{save.data.note}</span> : null}
        </div>
      ) : null}
    </section>
  );
}

// ── administrators ──────────────────────────────────────────────────────────

function AdministratorsPane({ detail }: { detail: OrganizationDetail }): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { account } = useAuth();
  const [accountId, setAccountId] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['organization', detail.id] });
  };

  const adminList = detail.administrators ?? [];
  const isCurrentAccountAdmin = adminList.some((a) => a.account_id === account?.id);

  const accountSuggestions = useQuery({
    queryKey: ['site-accounts-search', accountId],
    queryFn: () => fetchSiteAccounts(accountId.trim()),
    enabled: Boolean(account?.is_site_administrator && accountId.trim().length >= 1),
  });

  const assign = useMutation({
    mutationFn: (target?: string) => assignAdministrator(detail.id, (target ?? accountId).trim()),
    onSuccess: async () => {
      setAccountId('');
      setShowSuggestions(false);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeAdministrator(detail.id, id),
    onSuccess: refresh,
  });

  const error = [assign.error, revoke.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  return (
    <section className="org__card">
      <p className="org__eyebrow">{t('organizations.admin.tab_admins')}</p>
      <p className="org__fine">{t('organizations.admin.admins_desc')}</p>

      <ul className="org__list">
        {adminList.map((administrator) => {
          const rawId = administrator.account_id ?? '';
          const assignedAtStr = administrator.assigned_at ?? '';
          const dateStr = assignedAtStr ? String(assignedAtStr).slice(0, 10) : '';

          return (
            <li key={rawId} className="org__row">
              <div className="org__row-main">
                <div className="org__row-name">
                  <strong>{administrator.display_name}</strong>
                  {rawId === account?.id ? (
                    <span className="org-pill org-pill--approved" style={{ marginLeft: 8 }}>
                      {t('organizations.admin.you')}
                    </span>
                  ) : null}
                </div>
                <div className="org__row-meta">
                  ID: {rawId}
                  {dateStr ? ` · ${t('organizations.admin.since', { date: dateStr })}` : ''}
                </div>
              </div>

              {detail.may_administer ? (
                <button
                  type="button"
                  className="org__btn org__btn--quiet"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(rawId)}
                >
                  {t('organizations.admin.revoke')}
                </button>
              ) : (
                <span />
              )}
            </li>
          );
        })}
      </ul>

      {adminList.length === 0 ? (
        <p className="org__body">{t('organizations.admin.nobody_administers')}</p>
      ) : null}

      {detail.may_administer ? (
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border-soft)' }}>
          {account && !isCurrentAccountAdmin ? (
            <div
              style={{
                marginBottom: 14,
                padding: 12,
                background: 'var(--panel-warm)',
                border: '1px solid var(--gold-rule)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div>
                <p className="org__eyebrow" style={{ margin: 0 }}>
                  {t('organizations.admin.quick_assignment')}
                </p>
                <p style={{ fontSize: 13, margin: '3px 0 0' }}>
                  {t('organizations.admin.make_yourself_admin', {
                    name: account.display_name,
                    email: account.email,
                  })}
                </p>
              </div>
              <button
                type="button"
                className="org__btn"
                disabled={assign.isPending}
                onClick={() => assign.mutate(account.id)}
              >
                {assign.isPending
                  ? t('organizations.admin.assigning')
                  : t('organizations.admin.assign_myself')}
              </button>
            </div>
          ) : null}

          <label className="org__field" style={{ position: 'relative' }}>
            <span className="org__field-label">{t('organizations.admin.search_assign_label')}</span>
            <input
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder={
                account?.is_site_administrator
                  ? t('organizations.admin.search_placeholder_site_admin')
                  : t('organizations.admin.search_placeholder_org_admin')
              }
            />
            <span className="org__fine">
              {account?.is_site_administrator
                ? t('organizations.admin.search_fine_site_admin')
                : t('organizations.admin.search_fine_org_admin')}
            </span>

            {showSuggestions && (accountSuggestions.data?.data ?? []).length > 0 ? (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 30,
                  background: 'var(--panel)',
                  border: '1px solid var(--gold-rule)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {(accountSuggestions.data?.data ?? []).map((acc) => (
                  <button
                    key={acc.id}
                    type="button"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      borderBottom: '1px solid var(--border-soft)',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setAccountId(acc.email || acc.id);
                      setShowSuggestions(false);
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{acc.display_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-strong)' }}>
                      {acc.email} · ID: {acc.id}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </label>

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          <div className="org__actions">
            <button
              type="button"
              className="org__btn"
              disabled={assign.isPending || accountId.trim() === ''}
              onClick={() => assign.mutate(accountId.trim())}
            >
              {assign.isPending
                ? t('organizations.admin.assigning')
                : t('organizations.admin.appoint_admin')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
